import type { ComponentType, ReactNode } from "react";

import {
  type ExternalResource,
  type AnyFintechOfferSelection,
  SelectedStatus,
} from "@b2bportal/core-types";
import type { Prices } from "@hopper-b2b/types";

export interface FintechMarketplaceConfig<T extends string = string> {
  products: { [key in T]: ComponentType<FintechMarketplaceProductProps> };
  productsOrder: T[];
  useInitProducts: UseInitProducts<T>;
  canContinue: FintechMarketplaceConfigCanContinueFn;
  PageWrapper: ComponentType<FintechMarketplacePageWrapperProps>;
  TripDetails: ComponentType;
  PriceBreakdown: ComponentType<FintechMarketplacePriceBreakdownProps>;
  onContinue?: (productsSelection: FintechMarketplaceProductsSelection) => void;
  productViewType?: FintechMarketplaceProductViewType;
  productsBreadcrumbs?: {
    [key in T]?: ReactNode;
  };
}

export enum FintechMarketplaceProductViewType {
  STACK = "stack",
  INDIVIDUAL = "individual",
}

export type UseInitProducts<T extends string> = () => InitProductsState<T>;

export interface InitProductsState<T extends string> {
  productOffers: ProductOffers;
  fetchProductOffers: { [key in T]: () => void };
  productsSelection?: Partial<
    Record<T, FintechMarketplaceProductOfferSelection>
  >;
}

export type FintechMarketplaceProducts = ReadonlyMap<
  FintechMarketplaceProductKey,
  FintechMarketplaceProductConfig
>;

export type FintechMarketplaceProductConfig =
  ComponentType<FintechMarketplaceProductProps>;

export type FintechMarketplaceConfigCanContinueFn = (
  productsSelection: FintechMarketplaceProductsSelection
) => boolean;

export type FintechMarketplaceProductsSelection = Record<
  FintechMarketplaceProductKey,
  FintechMarketplaceProductOfferSelection
>;

export interface FintechMarketplaceProductProps<TOfferPayload = unknown> {
  offers: readonly FintechMarketplaceProductOfferMeta<TOfferPayload>[];
  /**
   * @deprecated Prefer providing pre-computed selection via
   * {@link InitProductsState.productsSelection} (the 3rd element of the
   * `useInit` tuple). This enables headless product views where the selection
   * is determined outside the marketplace UI (e.g. synthetic CFAR attached at
   * the fare level). `onSelect` is still called by the marketplace section for
   * interactive product views and will be removed once all products migrate to
   * the `productsSelection` approach.
   */
  onSelect: (offer: FintechMarketplaceProductOfferSelection) => void;
  productViewType: FintechMarketplaceProductViewType;
}

export type FintechMarketplaceProductOfferSelection =
  AnyFintechOfferSelection<SelectedFintechMarketplaceProductOfferMeta>;

export interface SelectedFintechMarketplaceProductOfferMeta
  extends FintechMarketplaceProductOfferMeta {
  title: string;
}

export interface FintechMarketplaceProductOfferMeta<TPayload = unknown> {
  premiumAmount: Prices;
  payload: TPayload;
}

export type ProductOffers = Record<
  FintechMarketplaceProductKey,
  ExternalResource<ReadonlyArray<FintechMarketplaceProductOfferMeta<unknown>>>
>;

export interface FintechMarketplacePriceBreakdownProps {
  productsSelection: FintechMarketplaceProductsSelection;
}

export const alwaysContinue: FintechMarketplaceConfigCanContinueFn = () => true;

export const continueWhenNoUnselected: FintechMarketplaceConfigCanContinueFn = (
  productsSelection
) =>
  Object.values(productsSelection).every(
    (p) => p.status !== SelectedStatus.UNSELECTED
  );

export type FintechMarketplaceProductKey = string;

export interface FintechMarketplacePageWrapperProps {
  children: ReactNode;
  onBack: () => void;
  className?: string;
}
